import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Boxes,
  CalendarClock,
  Cpu,
  Gauge,
  RefreshCw,
  Target,
  Wrench,
} from "lucide-react";
import { useApi } from "../hooks/useApi";

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------
const fmtInt = (v) =>
  v == null || v === "" || Number.isNaN(Number(v))
    ? "–"
    : Math.round(Number(v)).toLocaleString("en-US");

const fmtNum = (v, dec = 2) =>
  v == null || v === "" || Number.isNaN(Number(v))
    ? "–"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });

// Compact row counts for the artefact matrix: 31.7M, 22.8k, 483.
const fmtCompact = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "–";
  const n = Number(v);
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return String(Math.round(n));
};

const fmtBytes = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "–";
  const n = Number(v);
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)} kB`;
  return `${Math.round(n)} B`;
};

const fmtSecs = (s) => {
  if (s == null || Number.isNaN(Number(s))) return "–";
  const v = Math.max(0, Math.round(Number(s)));
  if (v < 120) return `${v}s`;
  const m = Math.floor(v / 60);
  if (m < 120) return `${m}m ${v % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

// timestamptz ISO -> "YYYY-MM-DD HH:MM" in Warsaw local time.
const fmtWaw = (iso) => {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Warsaw",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(d)
    .replace(",", "");
};

// "HH:MM" in Warsaw for the inline chip time ranges.
const fmtWawHM = (iso) => {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Warsaw",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
};

const utcDateStr = (d = new Date()) => d.toISOString().slice(0, 10);
const addDaysIso = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

// ---------------------------------------------------------------------------
// Small UI atoms
// ---------------------------------------------------------------------------
const LEVEL_CLS = {
  green: {
    light: "bg-emerald-50 border-emerald-300 text-emerald-700",
    dark: "bg-emerald-500/10 border-emerald-500/40 text-emerald-300",
  },
  amber: {
    light: "bg-amber-50 border-amber-300 text-amber-700",
    dark: "bg-amber-500/10 border-amber-500/40 text-amber-300",
  },
  red: {
    light: "bg-rose-50 border-rose-300 text-rose-700",
    dark: "bg-rose-500/10 border-rose-500/40 text-rose-300",
  },
  gray: {
    light: "bg-slate-50 border-slate-300 text-slate-600",
    dark: "bg-slate-800/60 border-slate-600 text-slate-300",
  },
};

const KpiChip = ({ label, value, sub, level = "gray", dark }) => (
  <div className={`rounded-xl border px-4 py-3 min-w-[9rem] ${LEVEL_CLS[level][dark ? "dark" : "light"]}`}>
    <div className={`text-[11px] font-medium uppercase tracking-wide ${dark ? "text-slate-400" : "text-slate-500"}`}>
      {label}
    </div>
    <div className="mt-0.5 text-lg font-bold leading-tight">{value}</div>
    {sub ? <div className={`mt-0.5 text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}>{sub}</div> : null}
  </div>
);

const SectionCard = ({ title, icon, children, dark, right }) => (
  <div className={`rounded-2xl border p-5 ${dark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200 shadow-sm"}`}>
    <div className="flex items-center justify-between mb-4">
      <h2 className={`flex items-center gap-2 text-sm font-semibold uppercase tracking-wide ${dark ? "text-slate-300" : "text-slate-600"}`}>
        {icon}
        {title}
      </h2>
      {right}
    </div>
    {children}
  </div>
);

// Nightly step chip colours by status.
const stepChipCls = (status, dark, active) => {
  const base = "px-2.5 py-1 rounded-md text-xs font-medium border cursor-pointer transition-colors select-none";
  const map = {
    ok: dark ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" : "bg-emerald-50 border-emerald-300 text-emerald-700",
    warn: dark ? "bg-amber-500/10 border-amber-500/40 text-amber-300" : "bg-amber-50 border-amber-300 text-amber-700",
    fail: dark ? "bg-rose-500/10 border-rose-500/40 text-rose-300" : "bg-rose-50 border-rose-300 text-rose-700",
    skip: dark ? "bg-slate-800/60 border-slate-600 text-slate-400" : "bg-slate-100 border-slate-300 text-slate-500",
  };
  return `${base} ${map[status] || map.skip} ${active ? "ring-2 ring-blue-400" : ""}`;
};


// Display names: 'sk_build' is the DATASET completeness check emitted inside
// train and candgen — it trains nothing. Renamed on screen because train-then-
// "build" read as if the model were built twice (user confusion 2026-07-28).
const STEP_LABEL = { sk_build: "sk_dataset" };

// One-glance chip subtitle: identity only (which model/version) — metrics live
// in the expanded box, keeping the collapsed strip to name + timestamp.
const chipInfo = (s) => {
  const t = String(s.summary || "");
  switch (s.step) {
    case "train": {
      const m = t.match(/SK (v[\d.]+)/);
      return m ? `SK ${m[1]}` : "SK model";
    }
    case "ceb_train": {
      const m = t.match(/(v[\d.]+)/);
      return `CEB G+D${m ? ` ${m[1]}` : ""}`;
    }
    default:
      return null;
  }
};

// Inline chip time range (Warsaw). Sub-minute steps show start time only.
const chipTimes = (s) => {
  const d = Number(s.duration_s) || 0;
  const a = fmtWawHM(s.started_at);
  return d < 60 ? a : `${a}–${fmtWawHM(s.finished_at)} · ${fmtSecs(d)}`;
};

// Parsed stats for the expanded box — label/value pairs beat scanning the raw
// summary line. Unknown formats simply yield no chips (raw summary still shown).
const chipStats = (s) => {
  const t = String(s.summary || "");
  const g = (re) => (t.match(re) || [])[1] || null;
  const out = [];
  const push = (label, v) => v != null && out.push([label, v]);
  switch (s.step) {
    case "train":
      push("model", g(/SK (v[\d.]+)/));
      push("data to", g(/data-to (\d{4}-\d{2}-\d{2})/));
      push("OOS MAE", g(/oosMAE=([\d.]+)/));
      push("tail MAE", g(/tailMAE=([\d.]+)/));
      push("iters", g(/iters=(\d+)/));
      break;
    case "ceb_train":
      push("model", g(/(v[\d.]+)/) && `CEB ladder ${g(/(v[\d.]+)/)}`);
      push("data to", g(/data-to (\d{4}-\d{2}-\d{2})/));
      push("iters", g(/(G=\d+\/D=\d+)/));
      push("rows", g(/rows=([\d,]+)/));
      break;
    case "sk_build":
      push("days built", g(/built (\d+\/\d+)/));
      push("missing", g(/missing (\d+)/));
      push("missing days", g(/missing \d+ \(last 30d: \d+\): (.+)$/));
      break;
    case "sk_prequential":
      push("scored day", g(/day=(\d{4}-\d{2}-\d{2})/));
      push("MAE", g(/mae=([\d.]+)/));
      push("rows", g(/n=(\d+)/));
      break;
    case "candgen":
      push("days", g(/days=\[([^\]]*)\]/));
      push("candidates", g(/loaded ([\d,]+)/));
      push("ppro cov", g(/ppro cov ([\d.]+)/));
      break;
    case "backfill":
      push("days", g(/days=\[([^\]]*)\]/));
      push("fails", g(/fails=(\S+)/));
      break;
    default:
      break;
  }
  return out;
};

// Artefact kinds, in the fixed matrix row order, with typical healthy row
// magnitudes for tooltips/legend.
const ARTEFACT_KINDS = [
  { kind: "contracts", typical: "~130–150" },
  { kind: "trades", typical: "~10–25k" },
  { kind: "orders_raw", typical: "~13–32M" },
  { kind: "capacities", typical: "~500k" },
  { kind: "top_of_book_events", typical: "~1–3M" },
  { kind: "depth_events", typical: "~4–12M" },
  { kind: "orderbook_20_seq", typical: "~300–900M" },
  { kind: "trade_aggressor", typical: "= trades rows" },
];

// A SERVING model older than 36 h means last night's train failed. Staleness is
// only applied to the version the live trader serves (m.serving from the
// backend, joined off live_config); other versions render as frozen streams.
// Retired v1.0.0 bundles are filtered out by the backend query.
const MODEL_STALE_S = 36 * 3600;

// ---------------------------------------------------------------------------
// One live box (tester or trader) — the /health/live_check payload has a key
// per box since the trader box launch (2026-08-02); both answer one NOTIFY.
// ---------------------------------------------------------------------------
const LiveBoxCard = ({ title, subtitle, data, loading, dark, accent }) => {
  const [tobOpen, setTobOpen] = useState(false);
  const th = `px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide ${dark ? "text-slate-400" : "text-slate-500"}`;
  const td = `px-2 py-2 text-sm ${dark ? "text-slate-200" : "text-slate-700"}`;

  const fresh = !!data?.fresh;
  const bookAge = data?.book_age_s == null ? null : Number(data.book_age_s);
  const wsLevel = !fresh
    ? "red"
    : data?.ws_active && bookAge != null && bookAge < 120
    ? "green"
    : bookAge != null && bookAge < 900
    ? "amber"
    : "red";
  const bookLevel = bookAge == null ? "gray" : bookAge < 120 ? "green" : bookAge < 900 ? "amber" : "red";
  // Trades shards land every ~5 min. (spool_orders_age_s is deliberately NOT
  // shown — the WS order stream is a sparse stub, so its age says nothing.)
  const spoolTradesLevel = (s) => (s == null ? "gray" : Number(s) < 900 ? "green" : Number(s) < 3600 ? "amber" : "red");

  return (
    <div className={`rounded-xl border p-4 ${dark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-slate-50/50"}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className={`text-sm font-bold ${accent ? (dark ? "text-sky-300" : "text-sky-700") : dark ? "text-slate-200" : "text-slate-700"}`}>
            {title}
          </span>
          <span className={`ml-2 text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}>{subtitle}</span>
        </div>
        <span className={`text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
          {data?.checked_at_waw ? `checked ${data.checked_at_waw} (Warsaw)` : ""}
        </span>
      </div>
      {!fresh && data && !loading && (
        <div className={`mb-3 rounded-lg border px-3 py-2 text-sm ${LEVEL_CLS.red[dark ? "dark" : "light"]}`}>
          box did not answer the ping — showing last known status
        </div>
      )}
      {loading && !data ? (
        <div className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>Pinging…</div>
      ) : (
        <div className="flex flex-wrap gap-3">
          <KpiChip
            dark={dark}
            level={wsLevel}
            label="WS capture"
            value={data?.ws_active == null ? "–" : data.ws_active ? "active" : "down"}
            sub={fresh ? "answered ping" : "stale"}
          />
          <KpiChip dark={dark} level={bookLevel} label="Book age" value={fmtSecs(bookAge)} sub="healthy ≈ < 60 s" />
          <KpiChip
            dark={dark}
            level={spoolTradesLevel(data?.spool_trades_age_s)}
            label="Spool trades"
            value={fmtSecs(data?.spool_trades_age_s)}
            sub="shards every ~5 m"
          />
          <KpiChip
            dark={dark}
            level={data?.trader_active == null ? "gray" : data.trader_active ? "green" : "red"}
            label="Trader proc"
            value={data?.trader_active == null ? "–" : data.trader_active ? "active" : "inactive"}
          />
          {/* Kill/control agent (intraday-*-ctl): the website Kill/Start path runs through
              it — inactive = the Kill button would do nothing (SSM kill still works). */}
          <KpiChip
            dark={dark}
            level={data?.ctl_active == null ? "gray" : data.ctl_active ? "green" : "red"}
            label="Kill agent"
            value={data?.ctl_active == null ? "–" : data.ctl_active ? "active" : "DOWN"}
            sub="website Kill/Start path"
          />
          {/* msgs_rate_min: 0.0 = counter frozen ≥ 6 min = REAL feed stall (red);
              null = not enough signal yet (backend falls back). */}
          <KpiChip
            dark={dark}
            level={data?.msgs_rate_min == null ? "gray" : Number(data.msgs_rate_min) === 0 ? "red" : "green"}
            label="Msgs rate"
            value={data?.msgs_rate_min == null ? "–" : `${fmtInt(data.msgs_rate_min)}/min`}
            sub={
              data?.msgs_rate_min != null && Number(data.msgs_rate_min) === 0
                ? `feed stall — counter frozen ≥ 6 min · ${fmtCompact(data?.msgs)} since start`
                : `${fmtCompact(data?.msgs)} since worker start${data?.msgs_rate_fallback ? " · rate from prev check" : ""}`
            }
          />
          <KpiChip dark={dark} level="gray" label="tracked" value={fmtInt(data?.tracked)} sub="contracts" />
          <KpiChip
            dark={dark}
            level="gray"
            label="WS capture up"
            value={data?.ws_started_at ? fmtSecs((Date.now() - new Date(data.ws_started_at).getTime()) / 1000) : "–"}
            sub={
              data?.ws_started_at ? (
                <>
                  since {fmtWaw(data.ws_started_at)} (Warsaw)
                  <br />
                  {data.last_reconnect_at &&
                  new Date(data.last_reconnect_at).getTime() !== new Date(data.ws_started_at).getTime()
                    ? `last feed (re)connect ≈ ${fmtWaw(data.last_reconnect_at)} (Warsaw)`
                    : "no feed reconnect since start"}
                </>
              ) : null
            }
          />
          <KpiChip
            dark={dark}
            level="gray"
            label="Trader up"
            value={
              data?.trader_started_at ? fmtSecs((Date.now() - new Date(data.trader_started_at).getTime()) / 1000) : "–"
            }
            sub={data?.trader_started_at ? `since ${fmtWaw(data.trader_started_at)} (Warsaw)` : null}
          />
        </div>
      )}
      {data?.tob?.samples?.length ? (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setTobOpen((v) => !v)}
            className={`text-[11px] font-semibold uppercase tracking-wide underline-offset-2 hover:underline ${dark ? "text-slate-400" : "text-slate-500"}`}
          >
            {tobOpen ? "hide" : "show"} top-of-book sample ({data.tob.samples.length} contracts)
          </button>
          {tobOpen && (
            <div className="overflow-x-auto mt-1.5">
              <table className="min-w-[32rem]">
                <thead>
                  <tr className={dark ? "border-b border-slate-700" : "border-b border-slate-200"}>
                    <th className={th}>Contract</th>
                    <th className={th}>Delivery</th>
                    <th className={`${th} text-right`}>Best bid</th>
                    <th className={`${th} text-right`}>Best ask</th>
                    <th className={`${th} text-right`}>Depth (b/a)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tob.samples.map((s) => (
                    <tr key={s.contract_id} className={dark ? "border-b border-slate-800" : "border-b border-slate-100"}>
                      <td className={`${td} font-semibold whitespace-nowrap`} title={s.contract_id}>
                        {s.product || s.name || s.contract_id}
                      </td>
                      <td className={`${td} whitespace-nowrap`}>{fmtWaw(s.delivery_start)}</td>
                      <td className={`${td} text-right tabular-nums whitespace-nowrap ${dark ? "text-emerald-400" : "text-emerald-600"}`}>
                        {s.best_bid ? `${fmtNum(s.best_bid[0], 2)} × ${fmtNum(s.best_bid[1], 1)} MW` : "–"}
                      </td>
                      <td className={`${td} text-right tabular-nums whitespace-nowrap ${dark ? "text-rose-400" : "text-rose-600"}`}>
                        {s.best_ask ? `${fmtNum(s.best_ask[0], 2)} × ${fmtNum(s.best_ask[1], 1)} MW` : "–"}
                      </td>
                      <td className={`${td} text-right tabular-nums`}>
                        {fmtInt(s.n_bids)} / {fmtInt(s.n_asks)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className={`mt-1 text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
                book at {fmtWaw(data.tob.book_ts)} (Warsaw)
                {data.tob.n_contracts != null ? ` · ${fmtInt(data.tob.n_contracts)} contracts quoted` : ""}
              </div>
            </div>
          )}
        </div>
      ) : null}
      {data?.note ? (
        <div className={`mt-3 rounded-lg px-3 py-2 font-mono text-[11px] whitespace-pre-wrap break-all ${dark ? "bg-slate-800/70 text-slate-400" : "bg-slate-100 text-slate-500"}`}>
          {data.note}
        </div>
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
const Health = ({ darkMode: dark }) => {
  const { fetchGet, fetchPost, isAuthenticated } = useApi();

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [live, setLive] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedStep, setExpandedStep] = useState(null); // "run_day|n<idx>" / "run_day|m<idx>"
  const [manualOpen, setManualOpen] = useState({}); // run_day -> bool (show ad-hoc runs)

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await fetchGet("/health/summary");
      if (!res.ok) throw new Error(`summary HTTP ${res.status}`);
      const data = await res.json();
      setSummary(data);
      setError(null);
    } catch (e) {
      setError(`Failed to load health summary: ${e.message}`);
    } finally {
      setSummaryLoading(false);
    }
  }, [fetchGet]);

  // The on-demand live check: NOTIFY on the DB, live box answers in ~1 s.
  // Called on mount and via the Re-check button — never on an interval.
  const runLiveCheck = useCallback(async () => {
    setLiveLoading(true);
    try {
      const res = await fetchPost("/health/live_check", {});
      if (!res.ok) throw new Error(`live_check HTTP ${res.status}`);
      setLive(await res.json());
    } catch (e) {
      setLive({ fresh: false, error: e.message });
    } finally {
      setLiveLoading(false);
    }
  }, [fetchPost]);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadSummary();
    runLiveCheck();
  }, [isAuthenticated, loadSummary, runLiveCheck]);

  // --------------------------------------------------------------- nightly
  // Chips render in CHRONOLOGICAL order by started_at (not grouped by step):
  // with self-reporting steps a day can read e.g.
  //   pit → backfill(fail) → train(skip) → candgen(skip) → backfill(ok) → …
  // which tells the failure-then-heal story naturally (heal rows are tagged
  // [manual heal] in their summary). `orchestrator` is NOT a peer chip — its
  // only unique content is the 02:00 unit's overall outcome, rendered as a
  // muted footer; red only when it exists WITHOUT any step rows (= the night
  // crashed before doing anything).
  // source='manual' rows (ad-hoc / research runs that self-report, e.g. daytime
  // sk_build frame rebuilds on OBW) are split out of the strip — they used to
  // render as peer chips and made the nightly unreadable.
  const nightlyByDay = useMemo(() => {
    const m = new Map();
    (summary?.nightly || []).forEach((r) => {
      if (!m.has(r.run_day)) m.set(r.run_day, { steps: [], manual: [], orchestrator: [] });
      const g = m.get(r.run_day);
      (r.step === "orchestrator" ? g.orchestrator : r.source === "manual" ? g.manual : g.steps).push(r);
    });
    const byStart = (a, b) => String(a.started_at).localeCompare(String(b.started_at));
    return Array.from(m.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([day, g]) => ({
        day,
        steps: g.steps.slice().sort(byStart),
        manual: g.manual.slice().sort(byStart),
        orchestrator: g.orchestrator.slice().sort(byStart),
      }));
  }, [summary]);

  // ------------------------------------------------------------- artefacts
  const utcToday = utcDateStr();
  const utcYesterday = addDaysIso(utcToday, -1);

  const artefactDays = useMemo(() => {
    const set = new Set((summary?.artefacts || []).map((a) => a.day));
    set.add(utcYesterday); // may be entirely absent before tonight's 02:00 UTC run
    return Array.from(set).sort().reverse().slice(0, 7);
  }, [summary, utcYesterday]);

  const artefactByKey = useMemo(() => {
    const m = {};
    (summary?.artefacts || []).forEach((a) => {
      m[`${a.day}|${a.kind}`] = a;
    });
    return m;
  }, [summary]);

  // Per-kind max row count over the shown days — each matrix row is shaded
  // relative to its own max (magnitudes differ by 6 orders across kinds), so a
  // pale cell in an otherwise dark row is a low outlier at a glance.
  const rowMaxByKind = useMemo(() => {
    const m = {};
    ARTEFACT_KINDS.forEach(({ kind }) => {
      let max = 0;
      artefactDays.forEach((day) => {
        const a = artefactByKey[`${day}|${kind}`];
        if (a && a.status === "ok") max = Math.max(max, Number(a.rows) || 0);
      });
      m[kind] = max;
    });
    return m;
  }, [artefactDays, artefactByKey]);

  // "yesterday" fills in only after the 02:00 UTC nightly backfill step.
  const backfillLoggedToday = useMemo(
    () => (summary?.nightly || []).some((r) => r.step === "backfill" && r.run_day === utcToday),
    [summary, utcToday]
  );
  const awaitingTonight =
    artefactDays[0] === utcYesterday &&
    !backfillLoggedToday &&
    !ARTEFACT_KINDS.some(({ kind }) => artefactByKey[`${utcYesterday}|${kind}`]);

  const artefactCell = (day, kind, typical) => {
    const a = artefactByKey[`${day}|${kind}`];
    const status = a?.status || "missing";
    const tip = a
      ? `${kind} · ${day}\nrows: ${fmtInt(a.rows)} (typical ${typical})\nbytes: ${fmtBytes(a.bytes)}\nstatus: ${status}\nupdated: ${fmtWaw(a.updated_at)} (Warsaw)`
      : `${kind} · ${day}\nno artefact row (missing)\ntypical ${typical}`;
    let cls = dark ? "text-slate-200" : "text-slate-700";
    let style;
    if (status === "empty") cls = dark ? "bg-rose-500/20 text-rose-300" : "bg-rose-100 text-rose-700";
    else if (status === "missing")
      cls = `border ${dark ? "border-rose-500/60 text-rose-400/80" : "border-rose-400 text-rose-500"}`;
    else if (a) {
      // Sequential one-hue shading, row-relative: intensity = rows / row max.
      const max = rowMaxByKind[kind];
      const t = max > 0 ? Math.min(1, (Number(a.rows) || 0) / max) : 0;
      style = { backgroundColor: `rgba(42, 120, 214, ${(t * (dark ? 0.45 : 0.4)).toFixed(3)})` };
    }
    return (
      <td key={day} className="px-1 py-1 text-center" title={tip}>
        <span
          style={style}
          className={`inline-block w-full min-w-[3.5rem] rounded px-1.5 py-0.5 text-xs font-medium tabular-nums ${cls}`}
        >
          {a ? fmtCompact(a.rows) : "–"}
        </span>
      </td>
    );
  };

  // ---------------------------------------------------------------- render
  const th = `px-2 py-2 text-left text-[11px] font-semibold uppercase tracking-wide ${dark ? "text-slate-400" : "text-slate-500"}`;
  const td = `px-2 py-2 text-sm ${dark ? "text-slate-200" : "text-slate-700"}`;
  const mono = `font-mono text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`;

  return (
    <div className="space-y-5">
      {error && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${LEVEL_CLS.red[dark ? "dark" : "light"]}`}>{error}</div>
      )}

      {/* A0. Exchange maintenance banner — only when a window is active or near */}
      {(summary?.maintenance || []).map((m) => (
        <div
          key={m.id}
          className={`rounded-xl border px-4 py-3 text-sm flex items-center gap-3 ${
            LEVEL_CLS[m.in_progress ? "red" : "amber"][dark ? "dark" : "light"]
          }`}
        >
          <Wrench className="w-4 h-4 shrink-0" />
          <span>
            <span className="font-semibold">
              {m.in_progress ? "Exchange maintenance IN PROGRESS" : "Exchange maintenance scheduled"}
            </span>
            {" · "}
            {fmtWaw(m.start_ts)} → {fmtWaw(m.end_ts)} (Warsaw)
            {m.note ? ` · ${m.note}` : ""}
            {m.in_progress ? " — fills suspended on both boxes" : ""}
          </span>
        </div>
      ))}

      {/* A. Live boxes (one NOTIFY, both boxes answer) — trader first */}
      <SectionCard
        dark={dark}
        title="Live boxes"
        icon={<Activity className="w-4 h-4" />}
        right={
          <button
            type="button"
            onClick={runLiveCheck}
            disabled={liveLoading}
            className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
              dark
                ? "border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
                : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            } ${liveLoading ? "opacity-60" : ""}`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${liveLoading ? "animate-spin" : ""}`} />
            {liveLoading ? "Checking…" : "Re-check"}
          </button>
        }
      >
        {live?.error ? (
          <div className={`mb-3 rounded-lg border px-3 py-2 text-sm ${LEVEL_CLS.red[dark ? "dark" : "light"]}`}>
            live check failed: {live.error}
          </div>
        ) : null}
        <div className="grid gap-4 xl:grid-cols-2">
          <LiveBoxCard
            dark={dark}
            accent
            title="LIVE-TRADER"
            subtitle="prod box · shadow mode → intraday_trading"
            data={live?.trader}
            loading={liveLoading}
          />
          <LiveBoxCard
            dark={dark}
            title="LIVE-TESTER"
            subtitle="dev box · paper → intraday (also runs the nightly)"
            data={live?.tester}
            loading={liveLoading}
          />
        </div>
      </SectionCard>

      {/* A2. Decision cycles — the trader-specific health. (A shadow-parity
          panel briefly lived here — REMOVED 2026-08-03: the boxes are expected
          to serve different model versions long-run, so cross-box prediction
          comparison is misleading.) */}
      <SectionCard dark={dark} title="Trading cycles" icon={<Gauge className="w-4 h-4" />}>
        <div className="grid gap-4 xl:grid-cols-2">
          {[
            ["trader", "LIVE-TRADER", true],
            ["tester", "LIVE-TESTER", false],
          ].map(([box, label, accent]) => {
            const c = summary?.cycles?.[box] || {};
            const last = c.last;
            const day = c.day;
            const age = last?.cycle_age_s == null ? null : Number(last.cycle_age_s);
            // cycles fire on the price_cost watermark ~every 15 min; > 30 min
            // without one = the trigger or the trader itself is stuck.
            const hbLevel = age == null ? "gray" : age < 1800 ? "green" : age < 3600 ? "amber" : "red";
            const stLevel =
              last?.status === "ok"
                ? "green"
                : ["maintenance", "warmup", "disabled"].includes(last?.status)
                ? "amber"
                : last?.status
                ? "red"
                : "gray";
            return (
              <div key={box} className={`rounded-xl border p-4 ${dark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-slate-50/50"}`}>
                <div className={`mb-3 text-sm font-bold ${accent ? (dark ? "text-sky-300" : "text-sky-700") : dark ? "text-slate-200" : "text-slate-700"}`}>
                  {label}
                </div>
                <div className="flex flex-wrap gap-3">
                  <KpiChip
                    dark={dark}
                    level={hbLevel}
                    label="Last cycle"
                    value={age == null ? "–" : `${fmtSecs(age)} ago`}
                    sub={last ? `${fmtWaw(last.cycle_ts)} (Warsaw) · every ~15 m` : "no cycle_log rows"}
                  />
                  <KpiChip
                    dark={dark}
                    level={stLevel}
                    label="Status"
                    value={last?.status || "–"}
                    sub={
                      last
                        ? `${fmtInt(last.n_preds)} preds · ${fmtInt(last.n_trades)} trades · ${fmtSecs(last.duration_s)} cycle`
                        : null
                    }
                  />
                  <KpiChip
                    dark={dark}
                    level={day?.n_error > 0 || day?.n_feed_down > 0 ? "amber" : "gray"}
                    label="Last 24 h"
                    value={day ? `${fmtInt(day.n_ok)}/${fmtInt(day.n)} ok` : "–"}
                    sub={
                      day
                        ? `${fmtInt(day.n_error)} error · ${fmtInt(day.n_feed_down)} feed-down · ${fmtInt(day.n_maint)} maint/warmup`
                        : null
                    }
                  />
                  <KpiChip
                    dark={dark}
                    level="gray"
                    label="Trades 24 h"
                    value={day ? fmtInt(day.trades) : "–"}
                    sub={day?.avg_duration_s != null ? `avg cycle ${fmtSecs(day.avg_duration_s)}` : null}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* A3. Alerts — always visible, full history newest-first (user decision
          2026-08-10: the self-hiding 48h window read as "alerts broken"). */}
      <SectionCard dark={dark} title="Alerts (latest 30)" icon={<AlertTriangle className="w-4 h-4" />}>
        {(summary?.alerts || []).length === 0 && (
          <div className={`text-sm ${dark ? "text-slate-500" : "text-slate-400"}`}>
            No alerts recorded yet — state-change events (feed down/up, maintenance start/end,
            cycle errors) will appear here.
          </div>
        )}
          {(summary?.alerts || []).length > 0 && (
        <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={dark ? "border-b border-slate-700" : "border-b border-slate-200"}>
                  <th className={th}>When (Warsaw)</th>
                  <th className={th}>Box</th>
                  <th className={th}>Severity</th>
                  <th className={th}>Kind</th>
                  <th className={th}>Message</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.alerts || []).map((a, i) => (
                  <tr key={i} className={dark ? "border-b border-slate-800" : "border-b border-slate-100"}>
                    <td className={`${td} whitespace-nowrap tabular-nums`}>{fmtWaw(a.ts)}</td>
                    <td className={`${td} font-semibold ${a.box === "trader" ? (dark ? "text-sky-300" : "text-sky-700") : ""}`}>
                      {a.box}
                    </td>
                    <td className={td}>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                          a.severity === "critical"
                            ? dark
                              ? "bg-rose-500/20 text-rose-300"
                              : "bg-rose-100 text-rose-700"
                            : a.severity === "warning"
                            ? dark
                              ? "bg-amber-500/20 text-amber-300"
                              : "bg-amber-100 text-amber-700"
                            : dark
                            ? "bg-slate-700 text-slate-300"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {a.severity}
                      </span>
                    </td>
                    <td className={`${td} font-mono text-xs`}>{a.kind}</td>
                    <td className={`${td} text-xs`}>{a.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          <div className={`mt-2 text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
            State-change events only (feed down/up, maintenance start/end, cycle errors) — one row per transition,
            not per cycle. Full history, newest first.
          </div>
        </SectionCard>

      {/* B. Latest trained models */}
      <SectionCard dark={dark} title="Latest trained models" icon={<Cpu className="w-4 h-4" />}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={dark ? "border-b border-slate-700" : "border-b border-slate-200"}>
                <th className={th}>Model</th>
                <th className={th}>Version</th>
                <th className={th}>Trained at (Warsaw)</th>
                <th className={th}>Data through</th>
                <th className={`${th} text-right`}>Train rows</th>
                <th className={th}>Best iter</th>
                <th className={`${th} text-right`}>OOS MAE</th>
                <th className={`${th} text-right`}>Tail MAE (in-sample)</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.models || []).map((m) => {
                // Staleness only means anything for the SERVING version (the
                // channel the live trader loads). Non-serving versions are
                // frozen streams — grayed out, never stale-red.
                const stale = m.serving && Number(m.trained_age_s) > MODEL_STALE_S;
                const retired = m.serving === false;
                return (
                  <tr
                    key={`${m.kind}|${m.version}`}
                    className={`${dark ? "border-b border-slate-800" : "border-b border-slate-100"} ${
                      stale ? (dark ? "bg-rose-500/10" : "bg-rose-50") : retired ? "opacity-50" : ""
                    }`}
                  >
                    <td className={`${td} font-semibold`}>
                      {m.kind}
                      <div className={mono}>{m.s3_prefix}</div>
                    </td>
                    <td className={td}>
                      {m.version}
                      {m.serving ? (
                        <span className={`ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${dark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>
                          SERVING
                        </span>
                      ) : (
                        <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded ${dark ? "bg-slate-700 text-slate-400" : "bg-slate-100 text-slate-500"}`}>
                          frozen
                        </span>
                      )}
                    </td>
                    <td className={`${td} ${stale ? (dark ? "text-rose-300 font-semibold" : "text-rose-600 font-semibold") : ""}`}>
                      {fmtWaw(m.trained_at)}
                      {stale ? " ⚠ >36h" : ""}
                    </td>
                    <td className={td}>{m.data_through}</td>
                    <td className={`${td} text-right tabular-nums`}>{fmtInt(m.train_rows)}</td>
                    <td className={`${td} font-mono text-xs`} title={m.best_iteration || ""}>
                      {m.best_iteration
                        ? String(m.best_iteration).length > 40
                          ? `${String(m.best_iteration).slice(0, 40)}…`
                          : m.best_iteration
                        : "–"}
                    </td>
                    <td className={`${td} text-right tabular-nums font-semibold`}>
                      {m.val_oos_mae == null ? "–" : fmtNum(m.val_oos_mae, 1)}
                    </td>
                    <td className={`${td} text-right tabular-nums ${dark ? "text-slate-500" : "text-slate-400"}`}>
                      {fmtNum(m.val_metric, 2)}
                    </td>
                  </tr>
                );
              })}
              {!summaryLoading && (summary?.models || []).length === 0 && (
                <tr>
                  <td colSpan={8} className={`${td} text-center py-6 ${dark ? "text-slate-500" : "text-slate-400"}`}>
                    No model bundles found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* B2. SK model — performance on UNSEEN data (SK3: honest OOS + prequential) */}
      <SectionCard dark={dark} title="SK model — unseen-data performance" icon={<Target className="w-4 h-4" />}>
        {(() => {
          const oosRows = (summary?.sk_oos || []).filter((r) => r.val_oos_mae != null);
          const preq = summary?.prequential || [];
          // NULL-e trzeba odsiac PRZED Number(): Number(null) === 0 i przechodzi isFinite,
          // wiec puste noce weszlyby do mediany jako zera (tak wlasnie zepsula sie drabinka CEB).
          const numbers = (rows, field) =>
            rows.map((r) => r[field]).filter((v) => v != null).map(Number).filter(Number.isFinite);
          const medianOf = (a) => {
            if (!a.length) return null;
            const s = [...a].sort((x, y) => x - y);
            const m = Math.floor(s.length / 2);
            return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
          };
          const oosVals = numbers(oosRows, "val_oos_mae");
          const latestOos = oosVals.length ? oosVals[0] : null;
          const medOos = medianOf(oosVals.slice(1));
          // Czerwony zarezerwowany dla bledu albo zatrzymania promocji przez bramke (nizej).
          // Sam podwyzszony blad, ktory niczego nie zatrzymal, to bursztyn.
          const oosLevel =
            latestOos == null || medOos == null
              ? "gray"
              : latestOos > 1.05 * medOos
              ? "amber"
              : "green";
          // Gate chips report the LATEST night, not "any rejection in the 7-day window".
          // The trainer writes sk_gate / ceb_gate ONLY when it refuses to publish, so the mere
          // presence of a row used to keep the chip red for a week after the cause was fixed
          // (2026-09-11 envelope false positive was still red on 09-16 with both models serving).
          const nl = summary?.nightly || [];                       // ordered run_day DESC
          const lastNight = nl.length ? nl[0].run_day : null;
          const rowFor = (day, step) => nl.find((n) => n.run_day === day && n.step === step);
          const priorGates = (step) => nl.filter((n) => n.step === step && n.run_day !== lastNight);
          const trainRow = rowFor(lastNight, "train");
          const cebTrainRow = rowFor(lastNight, "ceb_train");
          const gateWarn = rowFor(lastNight, "sk_gate");            // refused LAST night
          const cebGateWarn = rowFor(lastNight, "ceb_gate");
          const gatePrior = priorGates("sk_gate");
          const cebPrior = priorGates("ceb_gate");
          const priorNote = (rows) =>
            rows.length
              ? `gate refused to publish on ${rows.map((r) => r.run_day).join(", ")}`
              : null;
          // model control (REMIT 5.6.5): trainer's nightly verdict + the same 30d-vs-60d numbers
          const ctlLog = (summary?.nightly || []).find((n) => n.step === "model_control");
          const ctl = Object.fromEntries((summary?.control || []).map((r) => [r.win, r]));
          const rc = ctl.recent, rf = ctl.ref;
          const drift = rc && rf && rc.mae != null && rf.mae != null && Number(rc.mae) > 1.15 * Number(rf.mae);
          const fmtAcc = (v) => (v == null ? "–" : `${(Number(v) * 100).toFixed(0)}%`);
          const ladVals = numbers(summary?.ladder_oos || [], "val_oos_mae");
          const ladLatest = ladVals.length ? ladVals[0] : null;
          const ladMed = medianOf(ladVals.slice(1));
          const ladN = ladVals.length;              // ile nocy ma w ogole zmierzony blad
          const envHash = oosRows[0]?.envelope_hash;
          return (
            <>
              <div className="flex flex-wrap gap-3 mb-4">
                <KpiChip
                  dark={dark}
                  label="OOS MAE (honest, latest bundle)"
                  value={latestOos == null ? "n/a yet" : fmtNum(latestOos, 1)}
                  sub={medOos == null ? "pre-refit model on its 6w tail" : `trailing median ${fmtNum(medOos, 1)}`}
                  level={oosLevel}
                />
                <KpiChip
                  dark={dark}
                  label="Prequential (deployed model, newest settled day)"
                  value={preq[0] ? fmtNum(preq[0].mae, 1) : "n/a yet"}
                  sub={preq[0] ? `${preq[0].data_day} · ${preq[0].bundle}` : "scored nightly before retrain"}
                  level={preq[0] ? "green" : "gray"}
                />
                <KpiChip
                  dark={dark}
                  label="Promotion gate (last night)"
                  value={!trainRow ? "n/a" : gateWarn ? "SKIPPED publish" : "published"}
                  sub={
                    !trainRow
                      ? `no nightly train result${lastNight ? ` for ${lastNight}` : ""}`
                      : gateWarn
                      ? `${gateWarn.run_day}: ${String(gateWarn.summary || "").slice(0, 60)}`
                      : [`${lastNight} published`, priorNote(gatePrior) || "no rejections in last 7 days"].join(" · ")
                  }
                  level={!trainRow ? "gray" : gateWarn ? "red" : gatePrior.length ? "amber" : "green"}
                />
                {/* REMIT 5.6.5 model control: last 30 settled days of the DEPLOYED bundles vs the
                    60 days before (MAE > 1.15x or floor-level dir-acc down > 3 pp = drift). The
                    trainer writes the same verdict to nightly_log every night (warn -> Telegram). */}
                <KpiChip
                  dark={dark}
                  label="Model control (30d vs prior 60d)"
                  value={!rc || !rf ? "n/a yet" : drift ? "DRIFT" : `MAE ${fmtNum(rc.mae, 0)} vs ${fmtNum(rf.mae, 0)}`}
                  sub={
                    rc && rf
                      ? `dir-acc BUY ${fmtAcc(rc.dir_acc_buy)}/${fmtAcc(rf.dir_acc_buy)} · SELL ${fmtAcc(rc.dir_acc_sell)}/${fmtAcc(rf.dir_acc_sell)} · ${rc.n}d/${rf.n}d`
                      : "needs ≥10 recent and ≥20 reference days"
                  }
                  level={!rc || !rf ? "gray" : ctlLog?.status === "warn" || drift ? "red" : "green"}
                />
                <KpiChip
                  dark={dark}
                  label="Retrain envelope"
                  value={envHash ? envHash.slice(0, 8) : "n/a"}
                  sub="params + features + bundled code; a change without approval blocks publish"
                  level={envHash ? "green" : "gray"}
                />
                {/* Colour follows the val MAE, which is what the label promises. A gate refusal
                    LAST night still forces red; older refusals only appear in the subtitle. */}
                <KpiChip
                  dark={dark}
                  label="CEB ladder val MAE (60d tail)"
                  value={ladLatest == null ? "n/a yet" : fmtNum(ladLatest, 1)}
                  sub={
                    cebGateWarn
                      ? `${cebGateWarn.run_day}: gate refused to publish`
                      : [
                          ladMed == null
                            ? `measured on ${ladN} night(s); gate arms at 4`
                            : `median of the ${ladN - 1} earlier nights ${fmtNum(ladMed, 1)} · gate blocks above 1.15×`,
                          cebPrior.length ? priorNote(cebPrior) : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                  }
                  level={
                    cebGateWarn
                      ? "red"
                      : !cebTrainRow
                      ? "gray"
                      : ladLatest == null || ladMed == null
                      ? "gray"
                      : ladLatest > 1.05 * ladMed || cebPrior.length
                      ? "amber"
                      : "green"
                  }
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="overflow-x-auto">
                  <div className={`${th} mb-1`}>Nightly bundles — honest OOS trend</div>
                  <table className="w-full">
                    <thead>
                      <tr className={dark ? "border-b border-slate-700" : "border-b border-slate-200"}>
                        <th className={th}>Trained</th>
                        <th className={th}>Data to</th>
                        <th className={th}>Ver</th>
                        <th className={`${th} text-right`}>OOS MAE</th>
                        <th className={`${th} text-right`}>In-sample</th>
                        <th className={`${th} text-right`}>Best iter</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(summary?.sk_oos || []).map((r, i) => (
                        <tr key={i} className={dark ? "border-b border-slate-800" : "border-b border-slate-100"}>
                          <td className={td}>{fmtWaw(r.trained_at)}</td>
                          <td className={td}>{r.data_through}</td>
                          <td className={`${td} font-mono text-xs`}>{String(r.version || "").replace(/^v/, "")}</td>
                          <td className={`${td} text-right tabular-nums font-semibold`}>
                            {r.val_oos_mae == null ? "–" : fmtNum(r.val_oos_mae, 1)}
                          </td>
                          <td className={`${td} text-right tabular-nums ${dark ? "text-slate-500" : "text-slate-400"}`}>
                            {fmtNum(r.val_metric, 1)}
                          </td>
                          <td className={`${td} text-right tabular-nums`}>{r.best_iteration ?? "–"}</td>
                        </tr>
                      ))}
                      {(summary?.sk_oos || []).length === 0 && (
                        <tr>
                          <td colSpan={6} className={`${td} text-center py-4 ${dark ? "text-slate-500" : "text-slate-400"}`}>
                            No SK bundles yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className={`${mono} mt-1`}>
                    OOS = early-stopped model scored on rows it never trained on; in-sample = legacy refit-tail
                    metric (flattered, display-only). Bundles before 2026-07-19 have no OOS value.
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <div className={`${th} mb-1`}>Prequential — deployed model vs each newly settled day</div>
                  <table className="w-full">
                    <thead>
                      <tr className={dark ? "border-b border-slate-700" : "border-b border-slate-200"}>
                        <th className={th}>Settled day</th>
                        <th className={th}>Bundle (deployed that night)</th>
                        <th className={`${th} text-right`}>MAE</th>
                        <th className={`${th} text-right`}>Rows</th>
                        <th className={`${th} text-right`} title="directional accuracy of rows the live floors would have traded: BUY when pred ≥ buy_floor (right if y > 0)">BUY acc (n)</th>
                        <th className={`${th} text-right`} title="SELL when pred ≤ −sell_floor (right if y < 0)">SELL acc (n)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preq.map((r, i) => (
                        <tr key={i} className={dark ? "border-b border-slate-800" : "border-b border-slate-100"}>
                          <td className={td}>{r.data_day}</td>
                          <td className={`${td} font-mono text-xs`}>{r.bundle}</td>
                          <td className={`${td} text-right tabular-nums font-semibold`}>{fmtNum(r.mae, 1)}</td>
                          <td className={`${td} text-right tabular-nums`}>{fmtInt(r.n)}</td>
                          <td className={`${td} text-right tabular-nums`}>
                            {r.dir_acc_buy == null ? "–" : `${fmtAcc(r.dir_acc_buy)} (${fmtInt(r.n_buy)})`}
                          </td>
                          <td className={`${td} text-right tabular-nums`}>
                            {r.dir_acc_sell == null ? "–" : `${fmtAcc(r.dir_acc_sell)} (${fmtInt(r.n_sell)})`}
                          </td>
                        </tr>
                      ))}
                      {preq.length === 0 && (
                        <tr>
                          <td colSpan={6} className={`${td} text-center py-4 ${dark ? "text-slate-500" : "text-slate-400"}`}>
                            Accumulates from the first nightly after 2026-07-19
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <div className={`${mono} mt-1`}>
                    Scored BEFORE each retrain on the day that just settled — a day the deployed model never saw.
                    Rising MAE across ALL vintages = hard regime; rising for one bundle = model regression.
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </SectionCard>

      {/* C. Nightly runs (last 7 days) */}
      <SectionCard
        dark={dark}
        title="Nightly runs (last 7 days)"
        icon={<CalendarClock className="w-4 h-4" />}
        right={<span className={`text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>all times Warsaw</span>}
      >
        <div className="space-y-2">
          {nightlyByDay.map(({ day, steps, manual, orchestrator }) => (
            <div key={day}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`w-24 shrink-0 text-sm font-semibold tabular-nums ${dark ? "text-slate-300" : "text-slate-600"}`}>
                  {day}
                </span>
                {steps.map((s, i) => {
                  const key = `${day}|n${i}`;
                  const active = expandedStep === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setExpandedStep(active ? null : key)}
                      className={`${stepChipCls(s.status, dark, active)} text-left`}
                      title={`${STEP_LABEL[s.step] || s.step}: ${s.status} · ${fmtWaw(s.started_at)} → ${fmtWaw(s.finished_at)} (Warsaw, ${fmtSecs(s.duration_s)})`}
                    >
                      <div className="leading-tight">{STEP_LABEL[s.step] || s.step}</div>
                      <div className="leading-tight text-[10px] font-normal opacity-75 tabular-nums">
                        {[chipInfo(s), chipTimes(s)].filter(Boolean).join(" · ")}
                      </div>
                    </button>
                  );
                })}
                {manual.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const open = !manualOpen[day];
                      setManualOpen((p) => ({ ...p, [day]: open }));
                      if (!open && String(expandedStep || "").startsWith(`${day}|m`)) setExpandedStep(null);
                    }}
                    className={`px-2.5 py-1 rounded-md text-xs border border-dashed transition-colors select-none ${
                      dark
                        ? "border-slate-600 text-slate-500 hover:text-slate-300"
                        : "border-slate-300 text-slate-400 hover:text-slate-600"
                    }`}
                    title="Ad-hoc / research runs that logged on this day — not part of the 02:00 pipeline"
                  >
                    {manualOpen[day] ? "hide manual" : `+${manual.length} manual`}
                  </button>
                )}
                {manualOpen[day] &&
                  manual.map((s, i) => {
                    const key = `${day}|m${i}`;
                    const active = expandedStep === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setExpandedStep(active ? null : key)}
                        className={`${stepChipCls(s.status, dark, active)} opacity-60 text-left`}
                        title={`[manual] ${STEP_LABEL[s.step] || s.step}: ${s.status} · ${fmtWaw(s.started_at)} → ${fmtWaw(s.finished_at)} (Warsaw, ${fmtSecs(s.duration_s)})`}
                      >
                        <div className="leading-tight">{STEP_LABEL[s.step] || s.step}</div>
                        <div className="leading-tight text-[10px] font-normal opacity-75 tabular-nums">
                          {[chipInfo(s), chipTimes(s)].filter(Boolean).join(" · ")}
                        </div>
                      </button>
                    );
                  })}
              </div>
              {orchestrator.map((o, i) => {
                // Red only when the night crashed before any step self-reported.
                const crashed = steps.length === 0;
                return (
                  <div
                    key={`${day}-orch-${i}`}
                    className={`ml-24 mt-1 text-[11px] ${
                      crashed
                        ? dark
                          ? "text-rose-300 font-semibold"
                          : "text-rose-600 font-semibold"
                        : dark
                          ? "text-slate-500"
                          : "text-slate-400"
                    }`}
                    title={`orchestrator: ${o.status} · ${fmtWaw(o.started_at)} → ${fmtWaw(o.finished_at)} (Warsaw)`}
                  >
                    02:00 run: {o.summary || o.status}
                    {crashed ? " — crashed before any step ran" : ""}
                  </div>
                );
              })}
              {[...steps.map((s, i) => [s, `${day}|n${i}`, false]),
                ...manual.map((s, i) => [s, `${day}|m${i}`, true])].map(([s, key, isManual]) => {
                if (expandedStep !== key) return null;
                return (
                  <div
                    key={`${key}-detail`}
                    className={`mt-2 ml-24 rounded-lg px-3 py-2 ${dark ? "bg-slate-800/70" : "bg-slate-50"}`}
                  >
                    <div className={`text-[11px] mb-1 ${dark ? "text-slate-500" : "text-slate-400"}`}>
                      {isManual ? "[manual] " : ""}
                      {STEP_LABEL[s.step] || s.step} · {s.status} · {fmtWaw(s.started_at)} → {fmtWaw(s.finished_at)} (Warsaw, {fmtSecs(s.duration_s)})
                    </div>
                    {(() => {
                      const st = chipStats(s);
                      return st.length ? (
                        <div className="flex flex-wrap gap-x-5 gap-y-1 mb-2">
                          {st.map(([l, v]) => (
                            <div key={l}>
                              <span className={`text-[10px] uppercase tracking-wide ${dark ? "text-slate-500" : "text-slate-400"}`}>
                                {l}{" "}
                              </span>
                              <span className={`text-xs font-semibold tabular-nums ${dark ? "text-slate-200" : "text-slate-700"}`}>
                                {v}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()}
                    <pre className={`whitespace-pre-wrap break-words font-mono text-xs ${dark ? "text-slate-300" : "text-slate-600"}`}>
                      {s.summary || "(no summary)"}
                    </pre>
                  </div>
                );
              })}
            </div>
          ))}
          {!summaryLoading && nightlyByDay.length === 0 && (
            <div className={`text-sm ${dark ? "text-slate-500" : "text-slate-400"}`}>No nightly runs logged</div>
          )}
        </div>
        <div className={`mt-4 text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
          Pipeline: pit → backfill → <span className="font-semibold">train</span> (trains the SK model) →{" "}
          <span className="font-semibold">ceb_train</span> (trains the CEB up/down activation ladder) → candgen.{" "}
          <span className="font-mono">sk_dataset</span> chips train nothing — they are the completeness check of the
          SK feature frame, emitted once inside train and once inside candgen; amber there = a few old days missing
          from the frame (accepted policy, non-blocking). Chips run left-to-right in the order the steps actually
          ran, so a failed attempt followed by a heal reads e.g. backfill(fail) → … → backfill(ok) (heal runs are
          tagged <span className="font-mono">[manual heal]</span> in their summary). If backfill fails for D-1, train and
          candgen show <span className="font-semibold">skip</span> — intended (no data → no training). The muted
          "02:00 run" line is the orchestrator's overall outcome. Ad-hoc/research runs that self-report (e.g. daytime
          sk_build frame rebuilds) are hidden behind the dashed{" "}
          <span className="font-mono">+N manual</span> toggle — they are not part of the nightly. Rows tagged{" "}
          <span className="font-mono">[journal backfill]</span> are July history reconstructed from systemd journals;
          backfill/candgen steps only exist from 2026-07-10.
        </div>
      </SectionCard>

      {/* D. Artefacts (last 7 days) */}
      <SectionCard dark={dark} title="Artefacts (last 7 days)" icon={<Boxes className="w-4 h-4" />}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={dark ? "border-b border-slate-700" : "border-b border-slate-200"}>
                <th className={th}>Kind</th>
                {artefactDays.map((day) => (
                  <th key={day} className={`${th} text-center`}>
                    {day.slice(5)}
                    {day === utcYesterday && awaitingTonight ? (
                      <div className={`normal-case font-normal text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
                        awaiting tonight's run
                      </div>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ARTEFACT_KINDS.map(({ kind, typical }) => (
                <tr key={kind} className={dark ? "border-b border-slate-800" : "border-b border-slate-100"}>
                  <td className={`${td} whitespace-nowrap font-mono text-xs`}>
                    {kind}
                    <span className={`ml-2 ${dark ? "text-slate-600" : "text-slate-300"}`}>{typical}</span>
                  </td>
                  {artefactDays.map((day) => artefactCell(day, kind, typical))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={`mt-3 text-[11px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
          Cell = row count, hover for bytes / updated. Blue shading = count relative to that kind's 7-day max — a
          pale cell in a dark row is a low outlier. Red fill = <span className="font-semibold">empty</span>, red
          outline = <span className="font-semibold">missing</span>. "Yesterday" fills in after the 02:00 UTC nightly
          backfill.
        </div>
      </SectionCard>
    </div>
  );
};

export default Health;
